'use client';

import { useEffect, useRef, useState } from 'react';
import { formatMoney } from '@/src/lib/money';
import type { MenuItem, MenuModifierGroup } from '@/src/lib/hospitality/wire';
import type { AddLineInput } from '@/src/lib/hospitality/orderWire';
import {
  fixedChoiceQuantity,
  itemIssues,
  previewUnitPrice,
  type GroupIssue,
  type ModifierChoice,
} from './model';

/**
 * Choosing a dish's options before it becomes a cart line.
 *
 * <p>Submission is blocked while any group is invalid — a `required` group with
 * nothing chosen, or a `quantityConfigurable` one outside its
 * `minQuantity`/`maxQuantity`. The button is `disabled` AND each offending
 * group carries its own message, because a disabled button with no explanation
 * is a dead end for anyone who cannot see which section is red.
 *
 * <p>A group that is not `quantityConfigurable` renders as radios: a
 * `KitComponent` slot takes exactly one product. A configurable one renders as
 * a number field per option, stepping by 1 but holding a decimal STRING —
 * quantities are scale 3 in this model and a `number` input's value is read as
 * text, never as `valueAsNumber`.
 */

export interface ModifierDialogProps {
  item: MenuItem;
  currency: string;
  locale: string;
  t: (key: string, params?: Record<string, string | number>) => string;
  onCancel: () => void;
  onAdd: (input: AddLineInput) => void;
  busy: boolean;
}

const ISSUE_KEY: Record<GroupIssue, string> = {
  required: 'hospitality.order.modifier.required',
  'below-min': 'hospitality.order.modifier.belowMin',
  'above-max': 'hospitality.order.modifier.aboveMax',
  unorderable: 'hospitality.order.modifier.unorderable',
};

export function ModifierDialog({ item, currency, locale, t, onCancel, onAdd, busy }: ModifierDialogProps) {
  const [choices, setChoices] = useState<ModifierChoice[]>(() => defaultChoices(item));
  const [note, setNote] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingId = `modifier-heading-${item.productId}`;

  // Focus moves into the dialog when it opens and Escape closes it: a modal a
  // keyboard cannot leave is a trap, and 2.2 AA does not forgive one.
  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const issues = itemIssues(item, choices);
  const blocked = Object.keys(issues).length > 0;
  const preview = previewUnitPrice(item, choices);

  const setSingle = (group: MenuModifierGroup, productId: number) => {
    setChoices((current) => [
      ...current.filter((c) => c.slotKey !== group.slotKey),
      { slotKey: group.slotKey, productId, quantity: fixedChoiceQuantity(group) },
    ]);
  };

  const setQuantity = (group: MenuModifierGroup, productId: number, quantity: string) => {
    setChoices((current) => {
      const rest = current.filter((c) => !(c.slotKey === group.slotKey && c.productId === productId));
      const trimmed = quantity.trim();
      if (!trimmed || trimmed === '0') return rest;
      return [...rest, { slotKey: group.slotKey, productId, quantity: trimmed }];
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl sm:rounded-lg"
      >
        <h3 id={headingId} className="text-lg font-semibold">
          {item.title}
        </h3>
        {item.description ? (
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{item.description}</p>
        ) : null}

        {item.modifierGroups.map((group) => {
          const issue = issues[group.slotKey];
          const groupId = `group-${item.productId}-${group.slotKey}`;
          return (
            <fieldset
              key={group.slotKey}
              className="mt-4 border-t pt-3"
              aria-describedby={issue ? `${groupId}-issue` : undefined}
            >
              <legend className="text-sm font-medium">
                {group.label}
                {group.required ? (
                  <span className="ml-1 text-[var(--color-text-secondary)]">
                    {t('hospitality.order.modifier.requiredLabel')}
                  </span>
                ) : null}
              </legend>

              {group.options.map((option) => {
                const chosen = choices.find(
                  (c) => c.slotKey === group.slotKey && c.productId === option.productId,
                );
                const inputId = `${groupId}-${option.productId}`;
                const price = formatMoney({ amount: option.price ?? '0', currency }, locale);
                return (
                  <div key={option.productId} className="mt-2 flex items-center gap-2">
                    {group.quantityConfigurable ? (
                      <input
                        id={inputId}
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        disabled={!option.orderable}
                        // The VALUE is a string all the way through. Reading
                        // `valueAsNumber` here is how a scale-3 quantity turns
                        // into a float that no longer sums exactly.
                        value={chosen?.quantity ?? ''}
                        onChange={(event) => setQuantity(group, option.productId, event.target.value)}
                        className="w-16 rounded border px-2 py-1 text-sm"
                      />
                    ) : (
                      <input
                        id={inputId}
                        type="radio"
                        name={groupId}
                        disabled={!option.orderable}
                        checked={!!chosen}
                        onChange={() => setSingle(group, option.productId)}
                      />
                    )}
                    <label htmlFor={inputId} className="flex-1 text-sm">
                      {option.label}
                      {!option.orderable ? (
                        <span className="ml-2 text-[var(--color-text-secondary)]">
                          {t('hospitality.order.unavailable')}
                        </span>
                      ) : null}
                    </label>
                    {option.price && price ? <span className="text-sm">{price}</span> : null}
                  </div>
                );
              })}

              {issue ? (
                <p id={`${groupId}-issue`} role="alert" className="mt-2 text-sm text-red-700">
                  {t(ISSUE_KEY[issue], {
                    min: group.minQuantity ?? '',
                    max: group.maxQuantity ?? '',
                  })}
                </p>
              ) : null}
            </fieldset>
          );
        })}

        <div className="mt-4 border-t pt-3">
          <label htmlFor={`note-${item.productId}`} className="block text-sm font-medium">
            {t('hospitality.order.lineNote')}
          </label>
          <textarea
            id={`note-${item.productId}`}
            value={note}
            maxLength={500}
            rows={2}
            onChange={(event) => setNote(event.target.value)}
            className="mt-1 w-full rounded border px-2 py-1 text-sm"
          />
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button type="button" onClick={onCancel} className="rounded border px-4 py-2 text-sm">
            {t('hospitality.order.cancel')}
          </button>
          <button
            type="button"
            disabled={blocked || busy || !item.orderable}
            onClick={() =>
              onAdd({
                productId: item.productId,
                quantity: '1',
                note: note.trim() ? note.trim() : null,
                modifiers: choices.map((choice) => ({
                  productId: choice.productId,
                  slotKey: choice.slotKey,
                  quantity: choice.quantity,
                })),
              })
            }
            className="rounded bg-black px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {preview
              ? t('hospitality.order.addWithPrice', {
                  price: formatMoney({ amount: preview, currency }, locale) ?? preview,
                })
              : t('hospitality.order.add')}
          </button>
        </div>
      </div>
    </div>
  );
}

/** A required, non-configurable group with exactly one orderable option is
 *  pre-selected — there is no choice to make and forcing one is friction. Any
 *  other group starts empty, so `required` genuinely blocks. */
function defaultChoices(item: MenuItem): ModifierChoice[] {
  const chosen: ModifierChoice[] = [];
  for (const group of item.modifierGroups) {
    if (!group.required || group.quantityConfigurable) continue;
    const orderable = group.options.filter((option) => option.orderable);
    if (orderable.length === 1) {
      chosen.push({
        slotKey: group.slotKey,
        productId: orderable[0].productId,
        quantity: fixedChoiceQuantity(group),
      });
    }
  }
  return chosen;
}
